'use client'

/**
 * 🎨 MECHAGORA THEME SYSTEM
 *
 * SINGLE SOURCE OF TRUTH for ALL theming in Mechagora.
 * Everything comes from ONE hook: useTheme()
 *
 * @author Mechagora Development Team
 * @version 5.2.0 - Fixed Theme Token Context (Light/Dark switching now works)
 */

import React, { createContext, useContext, useEffect, useLayoutEffect, useState, useMemo, useCallback, ReactNode } from 'react'
import { ConfigProvider, theme as antdTheme, type GlobalToken, type ThemeConfig, Button, Tooltip } from 'antd'
import { SunOutlined, MoonOutlined, SyncOutlined } from '@ant-design/icons'
import type { AliasToken } from 'antd/es/theme/internal'

// Redux integration imports
import {
  useAppSelector,
  useAppDispatch,
  hydrate,
  setMode,
  setColorTheme,
  updateConfig,
  setSystemPreference,
  setInitialized,
} from '@/shared/providers/store'
import type { ThemeMode as ReduxThemeMode, ResolvedTheme, ColorTheme as ReduxColorTheme } from '@/shared/types/theme.types'
import {
  readStoredTheme,
  writeStoredTheme,
  resolveThemeType,
} from '@/shared/theme/persistence/local-cache'

// Flagship night-desk CSS tokens (dark + data-desk-theme=espresso) — Ant ThemeSystem
import '@/shared/theme/styles/tokens.desk.css'
import '@/shared/theme/styles/tokens.flagship-desk.desk.css'
// ui-ux-pro-max foundation: motion · touch · focus · density · reduced-motion
import '@/shared/theme/styles/pro-max.foundation.css'
// Solid desk button craft (Ant + .desk-btn) — pro-max hierarchy
import '@/shared/theme/styles/buttons.desk.css'
// Theme definitions — single catalog SSOT (add themes in themeCatalog.ts)
import {
  THEME_REGISTRY,
  DEFAULT_COLOR_THEME,
  resolveColorTheme,
} from '@/shared/theme/catalog'
import type { ThemeDefinition } from '@/shared/theme/types/theme.types'

// Old oklchToHex function removed - now using automatic conversion in getThemeDefinition

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

// Use Redux types for consistency
export type ThemeMode = ReduxThemeMode
export type ThemeType = ResolvedTheme
export type ColorTheme = ReduxColorTheme

export interface ThemeState {
  mode: ThemeMode
  type: ThemeType
  colorTheme: ColorTheme
  isDark: boolean
  isLight: boolean
  isAuto: boolean
  token: GlobalToken
  antdConfig: ThemeConfig
}

export interface ThemeActions {
  setMode: (mode: ThemeMode) => void
  setColorTheme: (colorTheme: ColorTheme) => void
  toggle: () => void
  setLight: () => void
  setDark: () => void
  setAuto: () => void
}

export interface ScrollbarStyles {
  scrollbarWidth: string
  scrollbarColor: string
  // CSS custom properties for global CSS usage
  '--scrollbar-width'?: string
  '--scrollbar-height'?: string
  '--scrollbar-track-bg'?: string
  '--scrollbar-thumb-bg'?: string
  '--scrollbar-thumb-hover-bg'?: string
  '--scrollbar-radius'?: string
}

export interface ComponentStyles {
  textPrimary: { color: string }
  textSecondary: { color: string }
  textTertiary: { color: string }
  bgContainer: { backgroundColor: string }
  bgLayout: { backgroundColor: string }
  bgElevated: { backgroundColor: string }
  border: { borderColor: string }
  borderSecondary: { borderColor: string }
}

export interface ThemeUtils {
  // Theme detection
  detectThemeType: (token: GlobalToken) => ThemeType

  // Color utilities
  withOpacity: (color: string, opacity: string) => string
  getLuminance: (color: string) => number

  // Adaptive values
  getThemeOpacities: (themeType: ThemeType) => Record<string, string>
  getThemeShadows: (token: GlobalToken, themeType: ThemeType) => Record<string, string>
  getThemeFills: (token: GlobalToken, themeType: ThemeType) => Record<string, string>
  getThemeBorders: (token: GlobalToken, themeType: ThemeType) => Record<string, string>
  getThemeTexts: (token: GlobalToken, themeType: ThemeType) => Record<string, string>

  // Interactive states
  getInteractiveStyles: (state: 'default' | 'hover' | 'focus' | 'pressed' | 'disabled') => any
}

export interface MechagoraThemeContext extends ThemeState, ThemeActions {
  utils: ThemeUtils
  scrollbar: ScrollbarStyles
  styles: ComponentStyles
  lightToken: GlobalToken  // Always the light theme token for the current color theme
}

// ============================================================================
// THEME REGISTRY — imported from themeCatalog (SSOT)
// ============================================================================

/**
 * Convert OKLCH color to hex using proper color space conversion
 * Based on the official OKLCH to RGB conversion algorithm
 */
function convertOklchToHex(oklchString: string): string {
  // Extract OKLCH values from string like "oklch(0.45 0.15 220)" or "oklch(0.45 0.15 220 / 0.8)"
  const match = oklchString.match(/oklch\(([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*([\d.]+))?\)/);
  if (!match) {
    console.warn(`Invalid OKLCH format: ${oklchString}`);
    return oklchString; // Return as-is if not OKLCH
  }

  const [, L = 0, C = 0, H = 0, alpha] = match.map(Number);

  // If alpha is provided, we need to handle it differently since hex doesn't support alpha
  // For now, we'll convert to hex without alpha (browsers handle alpha separately via rgba() or css variables)

  // Convert OKLCH to OKLab
  const h = H * Math.PI / 180; // Convert hue to radians
  const a = C * Math.cos(h);
  const b = C * Math.sin(h);

  // Convert OKLab to linear RGB using the official matrix
  // OKLab to LMS matrix
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.2914855480 * b;

  // LMS to linear RGB
  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;

  // Linear RGB matrix transformation
  let r = +4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  let g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  let bl = -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s;

  // Convert linear RGB to sRGB (gamma correction)
  const gammaCorrect = (c: number) => {
    if (c <= 0.0031308) {
      return 12.92 * c;
    } else {
      return 1.055 * Math.pow(c, 1.0 / 2.4) - 0.055;
    }
  };

  r = gammaCorrect(r);
  g = gammaCorrect(g);
  bl = gammaCorrect(bl);

  // Clamp to 0-1 range and convert to 0-255
  r = Math.max(0, Math.min(1, r)) * 255;
  g = Math.max(0, Math.min(1, g)) * 255;
  bl = Math.max(0, Math.min(1, bl)) * 255;

  // If alpha is provided, return rgba format; otherwise return hex
  if (alpha !== undefined && alpha !== null && !isNaN(alpha)) {
    return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(bl)}, ${alpha})`;
  }

  // Convert to hex
  const toHex = (n: number) => Math.round(n).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(bl)}`;
}

/**
 * Recursively convert all OKLCH values in an object to hex (or rgba if alpha is present)
 */
function convertThemeColorsToHex(obj: any): any {
  if (typeof obj === 'string') {
    if (obj.startsWith('oklch(')) {
      return convertOklchToHex(obj);
    }
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(convertThemeColorsToHex);
  }

  if (typeof obj === 'object' && obj !== null) {
    const converted: any = {};
    for (const [key, value] of Object.entries(obj)) {
      converted[key] = convertThemeColorsToHex(value);
    }
    return converted;
  }

  return obj;
}

/**
 * Get theme definition for a specific color theme and variant with automatic OKLCH conversion
 */
function getThemeDefinition(colorTheme: ColorTheme, variant: ThemeType): ThemeDefinition {
  const safeId = resolveColorTheme(colorTheme)
  const pair = THEME_REGISTRY[safeId] ?? THEME_REGISTRY[DEFAULT_COLOR_THEME]
  const rawTheme = pair[variant]
  // Ensure definition.id is the canonical Mechagora id (not a legacy alias)
  const withId = { ...rawTheme, id: safeId }

  // Convert all OKLCH colors to hex automatically
  return convertThemeColorsToHex(withId);
}

// Function moved to top of file to fix initialization order

/**
 * Apply theme CSS variables to document root
 */
function applyThemeVariables(colorTheme: ColorTheme, variant: ThemeType, token?: GlobalToken) {
  if (typeof window === 'undefined') return

  try {
    applyThemeVariablesUnsafe(colorTheme, variant, token)
  } catch (err) {
    // Never leave the desk half-themed — log and keep last good paint
    console.error('[ThemeProvider] applyThemeVariables failed', err)
  }
}

function applyThemeVariablesUnsafe(
  colorTheme: ColorTheme,
  variant: ThemeType,
  token?: GlobalToken
) {
  const themeDefinition = getThemeDefinition(colorTheme, variant)
  const root = document.documentElement

  // Apply all theme colors as CSS custom properties
  const colors = themeDefinition.colors

  // Primary colors
  root.style.setProperty('--color-primary', colors.primary.primary)
  root.style.setProperty('--color-primary-light', colors.primary.primaryLight)
  root.style.setProperty('--color-primary-dark', colors.primary.primaryDark)
  root.style.setProperty('--color-primary-hover', colors.primary.primaryHover)
  root.style.setProperty('--color-primary-active', colors.primary.primaryActive)

  // Generate RGB values for primary color (used in ShineBorder)
  const primaryRgb = hexToRgb(colors.primary.primary)
  root.style.setProperty('--color-primary-rgb', primaryRgb)

  // Semantic colors
  root.style.setProperty('--color-success', colors.semantic.success)
  root.style.setProperty('--color-success-light', colors.semantic.successLight)
  root.style.setProperty('--color-success-dark', colors.semantic.successDark)
  root.style.setProperty('--color-warning', colors.semantic.warning)
  root.style.setProperty('--color-warning-light', colors.semantic.warningLight)
  root.style.setProperty('--color-warning-dark', colors.semantic.warningDark)
  root.style.setProperty('--color-error', colors.semantic.error)
  root.style.setProperty('--color-error-light', colors.semantic.errorLight)
  root.style.setProperty('--color-error-dark', colors.semantic.errorDark)
  root.style.setProperty('--color-info', colors.semantic.info)
  root.style.setProperty('--color-info-light', colors.semantic.infoLight)
  root.style.setProperty('--color-info-dark', colors.semantic.infoDark)

  // Surface colors - already converted to hex by getThemeDefinition
  root.style.setProperty('--color-bg-container', colors.surface.card)
  root.style.setProperty('--color-bg-elevated', colors.surface.cardElevated)
  root.style.setProperty('--color-bg-layout', colors.surface.background)
  root.style.setProperty('--color-bg-mask', colors.surface.surfaceQuaternary)
  root.style.setProperty('--color-bg-spotlight', colors.surface.cardFloating)

  // Additional surface mappings - these are used extensively in CSS
  root.style.setProperty('--color-surface', colors.surface.background)
  root.style.setProperty('--color-surface-primary', colors.surface.surfacePrimary)
  root.style.setProperty('--color-surface-secondary', colors.surface.surfaceSecondary)
  root.style.setProperty('--color-surface-tertiary', colors.surface.surfaceTertiary)
  root.style.setProperty('--color-surface-quaternary', colors.surface.surfaceQuaternary)
  root.style.setProperty('--color-surface-hover', colors.surface.cardHover)

  // Text colors - already converted to hex by getThemeDefinition
  root.style.setProperty('--color-text', colors.text.textPrimary)
  root.style.setProperty('--color-text-primary', colors.text.textPrimary)
  root.style.setProperty('--color-text-secondary', colors.text.textSecondary)
  root.style.setProperty('--color-text-tertiary', colors.text.textTertiary)
  root.style.setProperty('--color-text-quaternary', colors.text.textQuaternary)
  root.style.setProperty('--color-text-disabled', colors.text.textDisabled)
  root.style.setProperty('--color-text-on-strong', colors.text.textOnStrong)
  root.style.setProperty('--color-text-on-light', colors.text.textOnLight)

  // Text aliases used in surface system
  root.style.setProperty('--text-on-strong', colors.text.textOnStrong)
  root.style.setProperty('--text-on-warning', colors.text.textOnStrong)

  // Border colors - already converted to hex by getThemeDefinition
  root.style.setProperty('--color-border', colors.border.border)
  root.style.setProperty('--color-border-secondary', colors.border.borderLight)

  // Accent colors - missing mapping that's used throughout the CSS
  root.style.setProperty('--color-accent', colors.border.accent)
  root.style.setProperty('--color-accent-light', colors.border.accentLight)
  root.style.setProperty('--color-accent-dark', colors.border.accentDark)

  // Generate RGB values from the converted hex accent color
  const accentHex = colors.border.accent
  const accentRgb = hexToRgb(accentHex)
  root.style.setProperty('--color-accent-rgb', accentRgb)

  // Seam color - used for dark theme visual effects
  root.style.setProperty('--seam-color', colors.border.accent)

  // Additional color mappings that are used in CSS but missing from mapping
  root.style.setProperty('--color-info', colors.semantic.info)
  root.style.setProperty('--color-info-light', colors.semantic.infoLight)
  root.style.setProperty('--color-info-dark', colors.semantic.infoDark)

  // Focus and ring colors (a11y — keyboard focus-visible uses --color-ring)
  root.style.setProperty('--color-focus', colors.border.borderFocus)
  root.style.setProperty('--color-focus-ring', colors.border.borderFocus)
  root.style.setProperty('--color-ring', colors.border.borderFocus || colors.primary.primary)
  // Muted surface + AA-safe muted foreground (secondary text, not tertiary greys)
  root.style.setProperty('--color-muted', colors.surface.surfaceTertiary || colors.surface.surfaceSecondary)
  root.style.setProperty('--color-muted-foreground', colors.text.textSecondary)
  root.style.setProperty('--color-foreground', colors.text.textPrimary)
  // on-primary: dark espresso ink on metal (pro-max AA) — never washed cream
  root.style.setProperty(
    '--color-on-primary',
    colors.text.textOnStrong || (variant === 'dark' ? colors.surface.background : colors.text.textPrimary) || '#0E0B09'
  )

  // Background colors - these are aliases used in some CSS
  root.style.setProperty('--color-background', colors.surface.background)

  // Input-specific colors for forms (pro-max: solid plate, AA placeholder)
  root.style.setProperty('--color-input-bg', colors.surface.card || colors.surface.surfaceSecondary)
  root.style.setProperty('--color-input-border', colors.border.border)
  root.style.setProperty('--color-input-border-hover', colors.primary.primaryHover)
  root.style.setProperty('--color-input-border-focus', colors.primary.primary)
  root.style.setProperty('--color-input-text', colors.text.textPrimary)
  root.style.setProperty('--color-input-placeholder', colors.text.textSecondary)

  // Card variants that exist in theme definitions
  root.style.setProperty('--color-card', colors.surface.card)
  root.style.setProperty('--color-card-hover', colors.surface.cardHover)
  root.style.setProperty('--color-card-active', colors.surface.cardActive)
  root.style.setProperty('--color-card-elevated', colors.surface.cardElevated)
  root.style.setProperty('--color-card-floating', colors.surface.cardFloating)

  // MASTER semantic aliases (tokens.css + CSS modules + Ant bridges)
  root.style.setProperty('--color-destructive', colors.semantic.error)
  // on-destructive: dark ink on light soft-red; cream on deep red (pro-max AA)
  const errorLum = getLuminance(colors.semantic.error)
  const onDestructive = errorLum > 0.35 ? '#0E0B09' : '#FFF9F4'
  root.style.setProperty('--color-on-destructive', onDestructive)
  root.style.setProperty('--color-on-accent', colors.text.textOnStrong || '#0E0B09')
  root.style.setProperty('--color-secondary', colors.primary.primaryLight)
  root.style.setProperty('--color-card-foreground', colors.text.textPrimary)
  root.style.setProperty('--color-destructive-rgb', hexToRgb(colors.semantic.error))
  // Keep muted-foreground on secondary (AA) — do not downgrade to tertiary

  // Pro-max surface aliases (foundation consumers)
  root.style.setProperty('--color-on-surface', colors.text.textPrimary)
  root.style.setProperty('--color-on-surface-variant', colors.text.textSecondary)
  root.style.setProperty('--color-outline', colors.border.border)
  root.style.setProperty('--color-outline-variant', colors.border.borderLight || colors.border.border)
  root.style.setProperty('--color-surface-container', colors.surface.card)
  root.style.setProperty('--color-surface-container-high', colors.surface.cardElevated)
  root.style.setProperty('--color-surface-container-low', colors.surface.background)
  root.style.setProperty('--focus-ring-color', colors.border.borderFocus || colors.primary.primary)
  root.style.setProperty('--text-on-strong', colors.text.textOnStrong || '#0E0B09')
  root.style.setProperty('--color-text-on-strong', colors.text.textOnStrong || '#0E0B09')

  // Tooltip popup — darker than navbar, solid metal rim, deep cast
  const tipBg = variant === 'dark' ? '#18120F' : '#1C1612'
  const tipFg = '#FBF7F2'
  root.style.setProperty('--tooltip-bg', tipBg)
  root.style.setProperty('--tooltip-fg', tipFg)
  // Solid metal rim both modes (light was diluted — looked borderless vs dark)
  root.style.setProperty('--tooltip-border', colors.primary.primary)
  root.style.setProperty(
    '--tooltip-shadow',
    variant === 'dark'
      ? `0 0 0 1px ${colors.primary.primary}55, 0 12px 36px rgba(0,0,0,0.65), 0 4px 12px rgba(0,0,0,0.45)`
      : `0 0 0 1px ${colors.primary.primary}66, 0 10px 28px rgba(18,14,11,0.28), 0 2px 8px rgba(18,14,11,0.14)`
  )
  // Toast / Ant message / notification share tooltip popup face + rim
  root.style.setProperty('--toast-bg', tipBg)
  root.style.setProperty('--toast-fg', tipFg)
  root.style.setProperty('--toast-border', colors.primary.primary)
  root.style.setProperty(
    '--toast-shadow',
    variant === 'dark'
      ? `0 0 0 1px ${colors.primary.primary}55, 0 14px 40px rgba(0,0,0,0.65), 0 4px 12px rgba(0,0,0,0.45)`
      : `0 0 0 1px ${colors.primary.primary}55, 0 12px 32px rgba(18,14,11,0.28), 0 4px 12px rgba(18,14,11,0.14)`
  )

  // -------------------------------------------------------------------------
  // SHELL SURFACES — single source: active theme (not hardcoded Mechagora hex)
  // Every chrome layer (sidebar / navbar / content well / workspace / footer /
  // subnav / scrollbar) derives from DeskToken surfaces so Midnight / Cream /
  // Accessible / custom stay coherent end-to-end.
  // -------------------------------------------------------------------------
  const isDarkShell = variant === 'dark'
  const canvas = colors.surface.background
  const container = colors.surface.card
  const elevated = colors.surface.cardElevated
  const tertiary = colors.surface.surfaceTertiary || container
  const secondary = colors.surface.surfaceSecondary || elevated
  const border = colors.border.border
  const borderLight = colors.border.borderLight || border
  const textPri = colors.text.textPrimary
  const textSec = colors.text.textSecondary
  const primary = colors.primary.primary

  // Content well + workspace = canvas; cards sit on that bay. Do not paint
  // the workspace the same color as --color-card or the desk reads as one slab.
  root.style.setProperty('--shell-content-bg', canvas)
  root.style.setProperty('--shell-content-edge', border)
  root.style.setProperty('--shell-workspace-bg', canvas)
  // Night header = espresso chrome flush to the rail; light = white 64px bar.
  const railBg = '#2A211C'
  root.style.setProperty('--shell-navbar-bg', isDarkShell ? railBg : elevated)
  root.style.setProperty('--shell-navbar-border', border)
  root.style.setProperty('--shell-navbar-shadow', 'none')
  root.style.setProperty('--shell-footer-bg', elevated)
  root.style.setProperty('--shell-footer-border', border)
  root.style.setProperty('--shell-footer-shadow', 'none')
  root.style.setProperty('--shell-subnav-bg', canvas)
  root.style.setProperty('--shell-subnav-border', border)

  // Rail — VISUAL LOCK: espresso #2A211C stays dark in BOTH desks (frame 01/02).
  // Header/footer share the rail plane — no split-tone cards, no drop shadow.
  root.style.setProperty('--shell-sidebar-bg', railBg)
  root.style.setProperty('--shell-sidebar-header', railBg)
  root.style.setProperty('--shell-sidebar-footer', railBg)
  root.style.setProperty(
    '--shell-sidebar-hover',
    `color-mix(in srgb, ${primary} ${isDarkShell ? '14%' : '16%'}, ${railBg})`
  )
  root.style.setProperty(
    '--shell-sidebar-active',
    `color-mix(in srgb, ${primary} ${isDarkShell ? '22%' : '24%'}, ${railBg})`
  )
  // Rail text stays cream in BOTH desks — the rail is always espresso #2A211C,
  // so dark theme text would be unreadable on it in light mode (frame 01 AA).
  root.style.setProperty('--shell-sidebar-text', isDarkShell ? textPri : '#FBF6F0')
  root.style.setProperty('--shell-sidebar-muted', isDarkShell ? textSec : '#C9B49E')
  root.style.setProperty('--shell-sidebar-border', border)
  root.style.setProperty('--shell-sidebar-shadow', 'none')
  root.style.setProperty(
    '--search-well-bg',
    isDarkShell
      ? 'color-mix(in srgb, #0E0B09 55%, #2A211C)'
      : 'color-mix(in srgb, var(--color-text-primary, #14100c) 5%, transparent)'
  )

  // Chrome aliases used by Navbar / AppChrome inline styles
  root.style.setProperty('--chrome-bg', isDarkShell ? railBg : elevated)
  root.style.setProperty('--chrome-border', border)
  root.style.setProperty('--chrome-text', textPri)
  root.style.setProperty('--chrome-text-muted', textSec)

  // Ant Design CSS-var bridges (modules + ConfigProvider consumers)
  root.style.setProperty('--ant-color-primary', primary)
  root.style.setProperty('--ant-color-error', colors.semantic.error)
  root.style.setProperty('--ant-color-info', colors.semantic.info)
  root.style.setProperty('--ant-color-warning', colors.semantic.warning)
  root.style.setProperty('--ant-color-success', colors.semantic.success)
  root.style.setProperty('--ant-color-bg-layout', canvas)
  root.style.setProperty('--ant-color-bg-container', container)
  root.style.setProperty('--ant-color-bg-elevated', elevated)
  root.style.setProperty('--ant-color-border', border)
  root.style.setProperty('--ant-color-border-secondary', borderLight)
  root.style.setProperty('--ant-color-text', textPri)
  root.style.setProperty('--ant-color-text-secondary', textSec)
  root.style.setProperty('--ant-color-text-tertiary', colors.text.textTertiary || textSec)
  root.style.setProperty('--ant-color-text-heading', textPri)
  root.style.setProperty('--ant-color-link', primary)

  // Ring beam colors for visual effects
  root.style.setProperty('--ring-beam-1', colors.primary.primary)
  root.style.setProperty('--ring-beam-2', colors.border.accent)
  root.style.setProperty('--ring-beam-3', colors.semantic.success)

  // Scrollbars always track the active shell well (no per-legacy-theme hex maps)
  const scrollbarTrack = canvas
  const scrollbarThumb = isDarkShell
    ? colors.surface.surfaceQuaternary || border
    : border
  const scrollbarThumbHover = primary

  root.style.setProperty('--scrollbar-width', '10px')
  root.style.setProperty('--scrollbar-height', '10px')
  root.style.setProperty('--scrollbar-track-bg', scrollbarTrack)
  root.style.setProperty('--scrollbar-thumb-bg', scrollbarThumb)
  root.style.setProperty('--scrollbar-thumb-hover-bg', scrollbarThumbHover)
  root.style.setProperty('--scrollbar-corner-bg', scrollbarTrack)
  root.style.setProperty('--scrollbar-radius', '999px')
  root.style.setProperty('--shell-scrollbar-track', scrollbarTrack)
  root.style.setProperty('--shell-scrollbar-thumb', scrollbarThumb)
  root.style.setProperty('--shell-scrollbar-thumb-hover', scrollbarThumbHover)
  root.style.setProperty('--shell-scrollbar-corner', scrollbarTrack)

  // Body/HTML scrollbar (larger)
  root.style.setProperty('--scrollbar-body-width', '12px')
  root.style.setProperty('--scrollbar-body-height', '12px')
  root.style.setProperty('--scrollbar-body-radius', '6px')

  // Set theme metadata
  root.style.setProperty('--theme-name', `"${themeDefinition.name}"`)
  root.style.setProperty('--theme-variant', `"${themeDefinition.variant}"`)
  root.style.setProperty('--active-theme', `"${themeDefinition.id}"`)
}

// ============================================================================
// THEME DETECTION & UTILITIES
// ============================================================================

function getLuminance(color: string): number {
  const hex = color.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16) / 255
  const g = parseInt(hex.substring(2, 4), 16) / 255
  const b = parseInt(hex.substring(4, 6), 16) / 255

  const sR = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4)
  const sG = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4)
  const sB = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4)

  return 0.2126 * sR + 0.7152 * sG + 0.0722 * sB
}

/**
 * Convert hex color to RGB string format for CSS variables
 */
function hexToRgb(hex: string): string {
  const cleanHex = hex.replace('#', '')
  const r = parseInt(cleanHex.substring(0, 2), 16)
  const g = parseInt(cleanHex.substring(2, 4), 16)
  const b = parseInt(cleanHex.substring(4, 6), 16)
  return `${r}, ${g}, ${b}`
}

function detectThemeType(token: GlobalToken): ThemeType {
  const bgLuminance = getLuminance(token.colorBgContainer || '#ffffff')
  return bgLuminance > 0.5 ? 'light' : 'dark'
}

function getSystemTheme(): ThemeType {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return 'light'
}

// ============================================================================
// THEME CONFIGURATIONS
// ============================================================================

// Theme definitions now accessed via THEME_REGISTRY and getThemeDefinition

// Dynamic theme configuration generator
function createThemeConfig(colorTheme: ColorTheme, variant: 'light' | 'dark'): ThemeConfig {
  // Use getThemeDefinition to get OKLCH colors converted to hex
  const themeDefinition = getThemeDefinition(colorTheme, variant)

  // Colors are now converted to hex by getThemeDefinition
  const primaryColor = themeDefinition.colors.primary.primary
  const successColor = themeDefinition.colors.semantic.success
  const warningColor = themeDefinition.colors.semantic.warning
  const errorColor = themeDefinition.colors.semantic.error
  const infoColor = themeDefinition.colors.semantic.info

  // Theme definition colors are now converted to hex
  const bgContainer = themeDefinition.colors.surface.card
  const bgElevated = themeDefinition.colors.surface.cardElevated
  const bgLayout = themeDefinition.colors.surface.background
  const textPrimary = themeDefinition.colors.text.textPrimary
  const textSecondary = themeDefinition.colors.text.textSecondary
  const textTertiary = themeDefinition.colors.text.textTertiary
  const textQuaternary = themeDefinition.colors.text.textQuaternary
  const borderColor = themeDefinition.colors.border.border
  const borderSecondary = themeDefinition.colors.border.borderLight

  // On-primary (pro-max AA): champagne/rose-gold always need dark espresso ink.
  // Cream-on-metal fails ~4.5:1 and reads as washed “flashlight” chrome.
  const primaryLum = getLuminance(primaryColor)
  const onStrong = themeDefinition.colors.text.textOnStrong
  const colorTextLightSolid =
    onStrong || (primaryLum > 0.35 ? '#0E0B09' : '#FFFCFA')
  /**
   * Tooltip = floating popup (must not match bay OR navbar).
   * Light: deep espresso + parchment.
   * Dark: darker-than-navbar plate + solid champagne rim (navbar is espresso #2A211C).
   */
  const tooltipBackground = variant === 'dark' ? '#18120F' : '#1C1612'
  const tooltipText = '#FBF7F2'
  // Soft primary wash (8-digit hex alpha; Ant Design FastColor-compatible)
  const colorPrimaryBg = variant === 'dark' ? `${primaryColor}29` : `${primaryColor}1A`
  const colorPrimaryBgHover = variant === 'dark' ? `${primaryColor}3D` : `${primaryColor}29`

  // Flagship desk craft: denser rack radii (not soft SaaS cards)
  const radiusSM = 4
  const radiusMD = 6
  const radiusLG = 8

  return {
    algorithm: variant === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: primaryColor,
      colorSuccess: successColor,
      colorWarning: warningColor,
      colorError: errorColor,
      colorInfo: infoColor,
      colorLink: primaryColor,
      colorLinkHover: themeDefinition.colors.primary.primaryHover,
      colorLinkActive: themeDefinition.colors.primary.primaryActive,
      // Surfaces — light cream / dark espresso; never pure #000
      colorBgBase: bgLayout,
      colorBgContainer: bgContainer,
      colorBgElevated: bgElevated,
      colorBgLayout: bgLayout,
      colorBgSpotlight: themeDefinition.colors.surface.cardFloating,
      colorBgMask: variant === 'dark' ? 'rgba(20, 16, 14, 0.78)' : 'rgba(42, 31, 26, 0.45)',
      // Text
      colorText: textPrimary,
      colorTextSecondary: textSecondary,
      colorTextTertiary: textTertiary,
      colorTextQuaternary: textQuaternary,
      colorTextLightSolid,
      // Borders + focus (MASTER ring = rose gold / champagne)
      colorBorder: borderColor,
      colorBorderSecondary: borderSecondary,
      colorPrimaryBg,
      colorPrimaryBgHover,
      // Instrument rack radii (flagship-v1 craft)
      borderRadius: radiusMD,
      borderRadiusLG: radiusLG,
      borderRadiusSM: radiusSM,
      borderRadiusXS: 2,
      // IBM Plex — Financial Trust (tokens.css)
      // Dashboard density: 14px body; labels ≥11px (AA floor for mono meta)
      fontFamily:
        "var(--font-sans), 'IBM Plex Sans', system-ui, -apple-system, 'Segoe UI', sans-serif",
      fontFamilyCode:
        "var(--font-mono), 'IBM Plex Mono', ui-monospace, 'SF Mono', monospace",
      fontSize: 14,
      fontSizeSM: 12,
      fontSizeLG: 16,
      fontSizeHeading1: 30,
      fontSizeHeading2: 24,
      fontSizeHeading3: 20,
      fontSizeHeading4: 16,
      fontSizeHeading5: 14,
      // pro-max body line-height 1.5; dense headings stay tight
      lineHeight: 1.5,
      lineHeightHeading1: 1.25,
      lineHeightHeading2: 1.25,
      lineHeightHeading3: 1.25,
      lineHeightHeading4: 1.35,
      lineHeightHeading5: 1.45,
      // Visual density 36–40; CSS enforces min 44 touch (pro-max.foundation + buttons)
      controlHeight: 40,
      controlHeightSM: 32,
      controlHeightLG: 48,
      controlOutline: variant === 'dark' ? `${primaryColor}40` : `${primaryColor}33`,
      controlOutlineWidth: 2,
    },
    components: {
      Button: {
        borderRadius: radiusSM + 1,
        primaryColor: colorTextLightSolid,
        algorithm: true,
        contentFontSizeSM: 12,
        fontWeight: 600,
        // Solid faces — no soft glow / washed gradients (buttons.desk.css is authority)
        defaultShadow: 'none',
        primaryShadow: 'none',
        dangerShadow: 'none',
        defaultBorderColor: borderColor,
        // Default = panel plate (secondary purpose). Primary = solid metal.
        defaultBg: bgContainer,
        defaultColor: textPrimary,
        defaultHoverBg: bgElevated,
        defaultHoverBorderColor: primaryColor,
        defaultHoverColor: textPrimary,
        defaultActiveBg: bgContainer,
        defaultActiveBorderColor: themeDefinition.colors.primary.primaryActive,
        controlHeight: 40,
        controlHeightSM: 32,
        controlHeightLG: 48,
        paddingInline: 16,
      },
      Input: {
        borderRadius: radiusSM,
        algorithm: true,
        colorBgContainer: themeDefinition.colors.surface.surfaceSecondary || bgContainer,
        colorText: textPrimary,
        colorTextPlaceholder: textTertiary,
        activeBorderColor: primaryColor,
        hoverBorderColor: themeDefinition.colors.primary.primaryHover,
        activeShadow:
          variant === 'dark' ? `0 0 0 2px ${primaryColor}40` : `0 0 0 2px ${primaryColor}33`,
        controlHeight: 40,
        controlHeightSM: 32,
      },
      Select: {
        borderRadius: radiusSM,
        colorBgContainer: themeDefinition.colors.surface.surfaceSecondary || bgContainer,
        colorBgElevated: bgElevated,
        colorText: textPrimary,
        colorTextPlaceholder: textTertiary,
        optionSelectedBg: colorPrimaryBg,
        optionSelectedColor: textPrimary,
        optionActiveBg: colorPrimaryBgHover,
        controlHeight: 40,
        controlHeightSM: 32,
      },
      Switch: {
        colorPrimary: primaryColor,
        colorPrimaryHover: themeDefinition.colors.primary.primaryHover,
      },
      Descriptions: {
        labelColor: textTertiary,
        contentColor: textPrimary,
        titleColor: textPrimary,
        extraColor: textSecondary,
        itemPaddingBottom: 8,
      },
      Badge: {
        colorSuccess: successColor,
        colorError: errorColor,
        colorWarning: warningColor,
        colorInfo: infoColor,
      },
      Card: {
        borderRadiusLG: radiusLG,
      },
      Modal: {
        borderRadiusLG: radiusLG + 2,
      },
      Segmented: {
        borderRadius: radiusSM,
        borderRadiusSM: 2,
        itemColor: textSecondary,
        itemHoverColor: textPrimary,
        itemSelectedColor: textPrimary,
        itemSelectedBg: variant === 'dark' ? bgElevated : bgContainer,
        trackBg:
          variant === 'dark'
            ? bgLayout
            : themeDefinition.colors.surface.surfaceSecondary,
        trackPadding: 2,
      },
      Tooltip: {
        borderRadius: radiusMD,
        colorBgSpotlight: tooltipBackground,
        colorTextLightSolid: tooltipText,
        // Ant 5 also reads these for inner face
        fontSize: 13,
      },
      // Book tables — espresso header + readable body ink (flagship workbench)
      Table: {
        borderRadius: radiusMD,
        headerBg:
          variant === 'dark'
            ? themeDefinition.colors.surface.surfaceSecondary
            : themeDefinition.colors.surface.surfaceTertiary,
        headerColor: variant === 'dark' ? textPrimary : textPrimary,
        headerSplitColor: borderColor,
        rowHoverBg:
          variant === 'dark' ? `${primaryColor}14` : `${primaryColor}0F`,
        borderColor: borderSecondary,
        colorBgContainer: bgContainer,
        colorText: textPrimary,
        colorTextHeading: textPrimary,
        fontFamily:
          "var(--font-mono), 'IBM Plex Mono', ui-monospace, monospace",
        cellFontSize: 13,
        cellFontSizeSM: 12,
        headerBorderRadius: 0,
      },
      Typography: {
        fontFamilyCode:
          "var(--font-mono), 'IBM Plex Mono', ui-monospace, monospace",
      },
      Tag: {
        borderRadiusSM: radiusSM,
        defaultColor: textSecondary,
        defaultBg:
          variant === 'dark'
            ? themeDefinition.colors.surface.surfaceTertiary
            : themeDefinition.colors.surface.surfaceSecondary,
      },
      Tabs: {
        itemColor: textSecondary,
        itemSelectedColor: textPrimary,
        itemHoverColor: textPrimary,
        inkBarColor: primaryColor,
        titleFontSize: 13,
      },
      Alert: {
        borderRadiusLG: radiusMD,
      },
    },
  }
}

// ============================================================================
// THEME UTILITIES FACTORY
// ============================================================================

function createThemeUtils(token: GlobalToken, themeType: ThemeType): ThemeUtils {
  const opacities = themeType === 'dark'
    ? { subtle: '08', light: '12', medium: '20', strong: '30', intense: '40', hover: '15', focus: '25', pressed: '35', disabled: '30' }
    : { subtle: '05', light: '08', medium: '15', strong: '25', intense: '35', hover: '10', focus: '20', pressed: '30', disabled: '30' }

  const shadows = {
    subtle: `0 1px 3px ${token.colorFillSecondary}${opacities.light}, 0 1px 2px ${token.colorFillTertiary}${opacities.subtle}`,
    elevated: `0 8px 28px ${token.colorPrimary}${opacities.subtle}, 0 4px 12px ${token.colorFillSecondary}${opacities.medium}, 0 2px 6px ${token.colorFillTertiary}${opacities.strong}, 0 1px 3px ${token.colorBorderSecondary}${opacities.light}`,
    focus: `0 0 0 2px ${token.colorPrimary}${opacities.focus}, 0 2px 6px ${token.colorPrimary}${opacities.light}`,
    hover: `0 2px 8px ${token.colorPrimary}${opacities.hover}, 0 1px 4px ${token.colorFillSecondary}${opacities.medium}`,
    pressed: `0 1px 2px ${token.colorPrimary}${opacities.pressed}, 0 1px 1px ${token.colorFillSecondary}${opacities.strong}`,
    inset: `inset 0 1px 0 ${token.colorPrimary}${opacities.light}, inset 0 -1px 0 ${token.colorPrimary}${opacities.light}`
  }

  const fills = {
    primarySubtle: `${token.colorPrimary}${opacities.subtle}`,
    primaryLight: `${token.colorPrimary}${opacities.light}`,
    primaryMedium: `${token.colorPrimary}${opacities.medium}`,
    primaryStrong: `${token.colorPrimary}${opacities.strong}`,
    hoverBg: themeType === 'dark' ? token.colorFillTertiary : token.colorFillSecondary,
    focusBg: `${token.colorPrimary}${opacities.subtle}`,
    selectedBg: token.colorPrimaryBg,
    pressedBg: `${token.colorPrimary}${opacities.light}`,
    containerElevated: token.colorBgElevated,
    containerSecondary: token.colorBgContainer,
    containerTertiary: token.colorBgLayout
  }

  const borders = {
    default: token.colorBorderSecondary,
    subtle: token.colorBorder,
    strong: token.colorBorderSecondary,
    primary: token.colorPrimary,
    primarySubtle: `${token.colorPrimary}${opacities.strong}`,
    focus: token.colorPrimary,
    error: token.colorError,
    success: token.colorSuccess,
    warning: token.colorWarning
  }

  const texts = {
    primary: token.colorTextHeading,
    secondary: token.colorTextSecondary,
    tertiary: token.colorTextTertiary,
    quaternary: token.colorTextQuaternary,
    disabled: token.colorTextDisabled,
    placeholder: token.colorTextPlaceholder,
    inverse: themeType === 'dark' ? token.colorBgContainer : token.colorTextHeading,
    accent: token.colorPrimary,
    success: token.colorSuccess,
    warning: token.colorWarning,
    error: token.colorError
  }

  return {
    detectThemeType,
    withOpacity: (color: string, opacity: string) => `${color}${opacity}`,
    getLuminance,
    getThemeOpacities: () => opacities,
    getThemeShadows: () => shadows,
    getThemeFills: () => fills,
    getThemeBorders: () => borders,
    getThemeTexts: () => texts,
    getInteractiveStyles: (state) => ({
      default: {
        backgroundColor: 'transparent',
        borderColor: borders.default,
        color: texts.primary,
        boxShadow: shadows.subtle
      },
      hover: {
        backgroundColor: fills.hoverBg,
        borderColor: borders.primarySubtle,
        color: texts.accent,
        boxShadow: shadows.hover
      },
      focus: {
        backgroundColor: fills.focusBg,
        borderColor: borders.focus,
        color: texts.accent,
        boxShadow: shadows.focus
      },
      pressed: {
        backgroundColor: fills.pressedBg,
        borderColor: borders.primary,
        color: texts.accent,
        boxShadow: shadows.pressed
      },
      disabled: {
        backgroundColor: token.colorBgContainerDisabled,
        borderColor: borders.subtle,
        color: texts.disabled,
        boxShadow: 'none',
        opacity: opacities.disabled
      }
    })[state]
  }
}

// ============================================================================
// THEME CONTEXT
// ============================================================================

const ThemeContext = createContext<MechagoraThemeContext | null>(null)

// ============================================================================
// THEME PROVIDER
// ============================================================================

interface ThemeProviderProps {
  children: ReactNode
  defaultMode?: ThemeMode
}

// Inner component that gets tokens from ConfigProvider
function ThemeProviderInner({
  children,
  mode,
  type,
  colorTheme,
  actions,
  dispatch
}: {
  children: React.ReactNode
  mode: ThemeMode
  type: ThemeType
  colorTheme: ColorTheme
  actions: ThemeActions
  dispatch: any
}) {
  // Get Ant Design token from ConfigProvider context
  const { token } = antdTheme.useToken()

  // Create theme utilities
  const utils = useMemo(() => createThemeUtils(token, type), [token, type])

  // Theme state with dynamic configuration
  const state: ThemeState = useMemo(() => ({
    mode,
    type,
    colorTheme,
    isDark: type === 'dark',
    isLight: type === 'light',
    isAuto: mode === 'auto',
    token,
    antdConfig: createThemeConfig(colorTheme, type)
  }), [mode, type, colorTheme, token])

  useLayoutEffect(() => {
    const root = document.documentElement
    root.setAttribute('data-theme', type)
    root.setAttribute('data-color-theme', colorTheme)
    root.classList.remove('light', 'dark', 'light-theme', 'dark-theme')
    root.classList.add(`${type}-theme`, type)
    root.style.colorScheme = type
    applyThemeVariables(colorTheme, type, token)
  }, [colorTheme, type, token])

  // Combined context value - ALL theme aspects in ONE place
  const contextValue: MechagoraThemeContext = useMemo(() => {
    const fills = utils.getThemeFills(token, type)
    const borders = utils.getThemeBorders(token, type)

    // Scrollbar colors from active desk tokens (same source as shell)
    const getProperScrollbarColors = () => {
      const isDark = type === 'dark'
      const themeDefinition = getThemeDefinition(colorTheme, type)
      const colors = themeDefinition.colors
      const track = colors.surface.background
      const thumb = isDark
        ? (colors.surface.surfaceQuaternary || colors.border.border)
        : colors.border.border
      return {
        track,
        thumb,
        thumbHover: colors.primary.primary,
        corner: track,
      }
    }

    const scrollbarColors = getProperScrollbarColors()

    // Generate scrollbar styles - CSS class-based approach for React compatibility
    const scrollbar: ScrollbarStyles = {
      scrollbarWidth: 'thin',
      scrollbarColor: `${scrollbarColors.thumb} ${scrollbarColors.track}`,
      // Note: CSS custom properties for use in global CSS
      '--scrollbar-width': '8px',
      '--scrollbar-height': '8px',
      '--scrollbar-track-bg': scrollbarColors.track,
      '--scrollbar-thumb-bg': scrollbarColors.thumb,
      '--scrollbar-thumb-hover-bg': scrollbarColors.thumbHover,
      '--scrollbar-radius': '4px',
    }

    // Generate component styles
    const styles: ComponentStyles = {
      textPrimary: { color: token.colorText },
      textSecondary: { color: token.colorTextSecondary },
      textTertiary: { color: token.colorTextTertiary },
      bgContainer: { backgroundColor: token.colorBgContainer },
      bgLayout: { backgroundColor: token.colorBgLayout },
      bgElevated: { backgroundColor: token.colorBgElevated },
      border: { borderColor: token.colorBorder },
      borderSecondary: { borderColor: token.colorBorderSecondary },
    }

    // Generate light theme token for current color theme
    const lightThemeConfig = createThemeConfig(colorTheme, 'light')
    const lightToken = lightThemeConfig.token as GlobalToken

    return {
      ...state,
      ...actions,
      utils,
      scrollbar,
      styles,
      lightToken
    }
  }, [state, actions, utils, token, type, colorTheme])

  // Apply theme to document
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const root = document.documentElement
      root.setAttribute('data-theme', type)
      root.setAttribute('data-color-theme', colorTheme)
      // ThemeSystem canonical classes: light-theme / dark-theme
      // Also bare light / dark for tokens.css (html.dark / html.light) and auth CSS
      root.classList.remove('light', 'dark', 'light-theme', 'dark-theme')
      root.classList.add(`${type}-theme`, type)
      root.style.colorScheme = type

      // Apply dynamic theme variables
      applyThemeVariables(colorTheme, type, token)
    }
  }, [type, colorTheme, token])

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  )
}

export function ThemeProvider({
  children,
  defaultMode = 'dark'
}: ThemeProviderProps) {
  // Use Redux for theme state management
  const dispatch = useAppDispatch()
  const reduxTheme = useAppSelector(state => state.theme)

  // Local system theme — SSR and first client paint stay light; OS preference applies after mount
  const [systemTheme, setSystemTheme] = useState<ThemeType>('light')
  const [hydrated, setHydrated] = useState(false)

  // Use Redux state values
  const mode = reduxTheme.mode
  const colorTheme = reduxTheme.colorTheme

  // Mount: prime OS preference, restore stored mode/colorTheme, keep currentTheme in sync
  useEffect(() => {
    if (typeof window === 'undefined') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const osTheme: ThemeType = mediaQuery.matches ? 'dark' : 'light'
    setSystemTheme(osTheme)
    dispatch(setSystemPreference(osTheme))

    const stored = readStoredTheme()
    if (stored) {
      dispatch(setMode(stored.mode))
      if (stored.colorTheme) {
        dispatch(setColorTheme(resolveColorTheme(stored.colorTheme)))
      }
    } else if (defaultMode && defaultMode !== 'auto') {
      dispatch(setMode(defaultMode))
    }

    dispatch(setInitialized(true))
    dispatch(hydrate({}))
    setHydrated(true)

    const handleChange = (e: MediaQueryListEvent) => {
      const next: ThemeType = e.matches ? 'dark' : 'light'
      setSystemTheme(next)
      dispatch(setSystemPreference(next))
    }
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [dispatch, defaultMode])

  // Persist preference whenever mode or colorTheme changes (after first hydrate)
  useEffect(() => {
    if (!hydrated) return
    writeStoredTheme({ mode, colorTheme })
  }, [mode, colorTheme, hydrated])

  // Determine current theme type (single resolution path)
  const type: ThemeType = resolveThemeType(mode, systemTheme)

  // Keep Redux currentTheme aligned with resolved type (auto tracks OS)
  useEffect(() => {
    if (!hydrated) return
    // setMode already updates currentTheme for light/dark; auto needs system sync
    if (mode === 'auto') {
      dispatch(setSystemPreference(systemTheme))
    }
  }, [mode, systemTheme, hydrated, dispatch])

  // Get dynamic Ant Design configuration based on selected color theme
  const antdConfig = createThemeConfig(colorTheme, type)

  // Theme actions using Redux. Toggle uses resolved `type` (what is on screen),
  // not Redux currentTheme which can lag OS priming for one frame on auto mode.
  const actions: ThemeActions = useMemo(() => ({
    setMode: (newMode: ThemeMode) => dispatch(setMode(newMode)),
    setColorTheme: (newColorTheme: ColorTheme) =>
      dispatch(setColorTheme(resolveColorTheme(newColorTheme))),
    toggle: () => dispatch(setMode(type === 'dark' ? 'light' : 'dark')),
    setLight: () => dispatch(setMode('light')),
    setDark: () => dispatch(setMode('dark')),
    setAuto: () => dispatch(setMode('auto'))
  }), [dispatch, type])

  return (
    <ConfigProvider theme={antdConfig}>
      <ThemeProviderInner
        mode={mode}
        type={type}
        colorTheme={colorTheme}
        actions={actions}
        dispatch={dispatch}
      >
        {children}
      </ThemeProviderInner>
    </ConfigProvider>
  )
}

// ============================================================================
// THE ONE AND ONLY THEME HOOK - NO MORE MULTIPLE CONTROLLERS!
// ============================================================================

/**
 * The ONLY theme hook you need!
 *
 * This replaces ALL separate theme hooks:
 * - useAntdTheme() ❌ ELIMINATED
 * - useScrollbarTheme() ❌ ELIMINATED
 * - useThemeUtils() ❌ ELIMINATED
 * - useThemeActions() ❌ ELIMINATED
 * - useThemeState() ❌ ELIMINATED
 *
 * Usage:
 * ```tsx
 * const theme = useTheme()
 *
 * // Theme state
 * theme.mode, theme.type, theme.isDark, theme.token
 *
 * // Theme actions
 * theme.setLight(), theme.setDark(), theme.toggle()
 *
 * // Utilities
 * theme.utils.getThemeFills(theme.token, theme.type)
 *
 * // Scrollbar styles (no separate hook!)
 * <div style={theme.scrollbar}>Content</div>
 *
 * // Component styles (no separate hook!)
 * <span style={theme.styles.textPrimary}>Text</span>
 * ```
 */
export function useTheme(): MechagoraThemeContext {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

// ============================================================================
// BACKWARD COMPATIBILITY EXPORTS (DEPRECATED)
// ============================================================================

// ============================================================================
// THEME TOGGLE COMPONENT
// ============================================================================

interface ThemeToggleProps {
  size?: 'small' | 'medium' | 'large'
  showLabel?: boolean
  className?: string
}

export function ThemeToggle({
  size = 'medium',
  showLabel = false,
  className = ''
}: ThemeToggleProps) {
  const { mode, toggle } = useTheme()

  const getIcon = () => {
    switch (mode) {
      case 'light': return <SunOutlined />   // Show sun for light mode
      case 'dark': return <MoonOutlined />   // Show moon for dark mode
      case 'auto': return <SyncOutlined />   // Show sync for auto mode
      default: return <SunOutlined />
    }
  }

  const getLabel = () => {
    switch (mode) {
      case 'light': return 'Light'
      case 'dark': return 'Dark'
      case 'auto': return 'Auto'
      default: return 'Light'
    }
  }

  const getTooltip = () => {
    switch (mode) {
      case 'light': return 'Currently light mode - click to switch to dark'
      case 'dark': return 'Currently dark mode - click to switch to light'
      case 'auto': return 'Auto mode - following system preference'
      default: return 'Toggle theme'
    }
  }

  const buttonSize = size === 'small' ? 'small' : size === 'large' ? 'large' : 'middle'

  const tooltip = getTooltip()
  // Icon-only by default — must expose an accessible name for screen readers
  const ariaLabel = showLabel
    ? `Color theme: ${getLabel()}. ${tooltip}`
    : tooltip

  return (
    <Tooltip title={tooltip}>
      <Button
        type="text"
        icon={getIcon()}
        onClick={toggle}
        size={buttonSize}
        className={`theme-toggle ${className}`}
        aria-label={ariaLabel}
        aria-pressed={mode === 'dark'}
      >
        {showLabel && getLabel()}
      </Button>
    </Tooltip>
  )
}

// ============================================================================
// EXPORTS
// ============================================================================

export default ThemeProvider

// Primary exports - clean naming without "Unified" prefix
// useTheme is already exported above as the main hook

// Re-exports for Ant Design integration
export type { GlobalToken, ThemeConfig, AliasToken }
export { antdTheme as theme }
